"use client";

import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import "./DoctorQuestionsCard.css";

import useLikePost from "../../hooks/useLikePost";
import useComments from "../../hooks/useComments";

import { fetchUserById } from "../../../Profile/redux/thunks/userThunks";
import { resolveDoctorAvatar } from "../../../ProfileDoctor/hooks/useDoctorInformation";
import { useUserInformation } from "../../../Profile/hooks/useUserInformation";
import { fetchDoctors } from "../../../authDoctor/redux/thunks/doctorsThunks";
import AIResponseBanner from "../shared/AIResponseBanner/AIResponseBanner";

/* ================= Constants ================= */
const DEFAULT_AVATAR = "https://cdn-icons-png.flaticon.com/512/149/149071.png";

/* ================= Helpers ================= */
const toKey = (v) => (v == null ? "" : String(v).trim());
const lower = (v) => toKey(v).toLowerCase();

const isDoctorComment = (comment) => {
  const role = String(
    comment?.role ||
      comment?.author?.role ||
      comment?.user?.role ||
      comment?.doctor?.role ||
      ""
  ).toLowerCase();

  return Boolean(
    comment?.doctorId ||
      comment?.doctor_id ||
      comment?.doctor?.id ||
      comment?.doctor?._id ||
      comment?.Doctor?.id ||
      comment?.Doctor?._id ||
      role.includes("doctor") ||
      role.includes("medecin") ||
      role.includes("médecin") ||
      role === "dr"
  );
};

const isUUID = (v) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(lower(v));

const isMongoId = (v) => /^[0-9a-f]{24}$/i.test(toKey(v));

// ✅ FIX: l'ancienne version (toIntOrUndef) rejetait tout ID qui n'était
// ni un UUID complet ni un entier positif. Un ObjectId Mongo (24 hex),
// ou un ID renvoyé sous une forme un peu différente, était donc silencieusement
// jeté => doctorId / postOwnerId restaient `undefined` => like, commentaires
// et résolution du nom utilisateur ne s'activaient jamais.
const toIdOrUndef = (v) => {
  if (v == null) return undefined;
  const s = toKey(v);
  if (!s) return undefined;
  const low = s.toLowerCase();
  if (["null", "undefined", "n/a", "na"].includes(low)) return undefined;
  if (isUUID(s)) return low;
  if (isMongoId(s)) return low;
  const n = Number(s);
  if (Number.isFinite(n) && n > 0) return n;
  // Dernier recours : ne pas jeter un ID valide juste parce que son format
  // ne correspond à aucun des cas connus.
  if (/^[a-zA-Z0-9_-]{2,}$/.test(s)) return s;
  return undefined;
};

// 🐛 FIX PRINCIPAL (ce ticket) : dépile un id qui peut être renvoyé comme
// objet plutôt que comme string/nombre — cas fréquent avec un document
// Mongo brut (non "lean()") : { _id: ObjectId(...) } ou { _id: { $oid: "..." } }.
// Avant : `String(objet)` produisait `"[object Object]"`, un id invalide
// mais "truthy", qui cassait silencieusement les appels API de like/commentaire.
const extractRawId = (v) => {
  if (v == null) return undefined;
  if (typeof v === "object") {
    if (v.$oid) return v.$oid;
    if (v._id) return extractRawId(v._id);
    if (v.id) return extractRawId(v.id);
    if (typeof v.toHexString === "function") return v.toHexString();
    if (typeof v.toString === "function") {
      const s = v.toString();
      if (s && s !== "[object Object]") return s;
    }
    return undefined;
  }
  return v;
};

const sameId = (a, b) => {
  const sa = lower(a);
  const sb = lower(b);
  if (!sa || !sb) return false;
  if (sa === sb) return true;
  const na = Number(sa);
  const nb = Number(sb);
  return Number.isFinite(na) && Number.isFinite(nb) ? String(na) === String(nb) : false;
};

const pickFullName = (u) => {
  if (!u) return "";
  return (
    u.fullName ||
    u.name ||
    u.username ||
    u.displayName ||
    u.patientName ||
    u.userName ||
    u.authorName ||
    u.author?.fullName ||
    u.author?.name ||
    u.user?.fullName ||
    u.user?.name ||
    `${u.firstName || ""} ${u.lastName || ""}`.trim() ||
    ""
  );
};

const pickAvatar = (u) =>
  u?.avatarUrl ||
  u?.avatar ||
  u?.photo ||
  u?.image ||
  u?.profilePicture ||
  u?.picture ||
  u?.raw?.avatarUrl ||
  u?.raw?.avatar ||
  u?.raw?.photo ||
  null;

const MAX_DATA_URL_LEN = 2_000_000;

const doctorsCache = { data: null, promise: null };
const patientUserCache = new Map();
const patientUserPromises = new Map();

// ✅ FIX (bug principal): cache négatif persistant (au niveau module, donc
// partagé par toutes les instances de la carte et survit aux re-renders).
// Avant, un échec de fetchUserById (404 "User not found") faisait
// `fetchedRef.current.delete(userIdToFetch)`, ce qui autorisait un nouveau
// fetch au prochain rendu (scroll, like, ouverture des commentaires...).
// Résultat visible dans les DevTools : la même requête vers /api/users/{id}
// repart en boucle et re-génère un 404 à chaque fois. On mémorise donc les
// IDs connus comme introuvables et on ne retente plus (sauf après un TTL,
// au cas où l'utilisateur serait recréé/republié).
const FAILED_USER_TTL_MS = 5 * 60 * 1000; // 5 minutes
const failedUserFetches = new Map(); // id -> timestamp du dernier échec

const isUserFetchBlacklisted = (id) => {
  const ts = failedUserFetches.get(id);
  if (!ts) return false;
  if (Date.now() - ts > FAILED_USER_TTL_MS) {
    failedUserFetches.delete(id);
    return false;
  }
  return true;
};

const markUserFetchFailed = (id) => failedUserFetches.set(id, Date.now());

const getMediaUrl = (photo) => {
  if (!photo) return null;
  try {
    const s = String(photo).trim();

    if (s.startsWith("data:image/")) {
      if (s.length < 50 || s.length > MAX_DATA_URL_LEN) return null;
      return s;
    }

    if (s.startsWith("http://") || s.startsWith("https://")) return s;

    if (/^[A-Za-z0-9+/]+=*$/.test(s) && s.length > 100 && s.length < MAX_DATA_URL_LEN) {
      const clean = s.replace(/[^A-Za-z0-9+/=]/g, "");
      atob(clean.slice(0, 64));
      return `data:image/jpeg;base64,${clean}`;
    }

    return null;
  } catch {
    return null;
  }
};

const SafeImage = ({ src, alt, className, fallback = DEFAULT_AVATAR, ...props }) => {
  const [state, setState] = useState({ loading: true, error: false, loaded: false });

  const processed = useMemo(() => {
    if (!src) return null;
    const s = String(src);
    if (s.startsWith("data:image/")) return s.length <= MAX_DATA_URL_LEN ? s : null;
    if (s.startsWith("http://") || s.startsWith("https://")) return s;
    return null;
  }, [src]);

  useEffect(() => setState({ loading: true, error: false, loaded: false }), [processed]);

  const finalSrc = processed || fallback;


  return (
    <div className="image-container">
      <img
        {...props}
        src={state.error ? fallback : finalSrc}
        alt={alt || "image"}
        className={`${className || ""} ${state.loaded ? "loaded" : "loading"}`}
        onLoad={() => setState({ loading: false, error: false, loaded: true })}
        onError={() => setState({ loading: false, error: true, loaded: false })}
        loading="lazy"
      />
    </div>
  );
};

const timeAgo = (d) => {
  if (!d) return "now";
  const secs = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
  const map = [
    ["y", 31536000],
    ["mo", 2592000],
    ["w", 604800],
    ["d", 86400],
    ["h", 3600],
    ["m", 60],
  ];
  for (const [k, v] of map) {
    const n = Math.floor(secs / v);
    if (n >= 1) return `${n}${k}`;
  }
  return "now";
};

const getCommentPatientId = (c) => {
  const cand = [
    c?.userId,
    c?.user_id,
    c?.patientId,
    c?.createdBy,
    c?.user?._id,
    c?.user?.id,
    c?.user?.userId,
    c?.author?._id,
    c?.author?.id,
  ];
  for (const x of cand) {
    const id = toIdOrUndef(extractRawId(x));
    if (id !== undefined) return lower(id);
  }
  return "";
};

// ✅ FIX: candidats élargis (patientId, post.patient.*, post.author.*)
// pour éviter de rater l'ID du patient à cause d'un nom de champ différent
// selon la route API qui a produit le post.
const getPostPatientId = (post) => {
  if (!post) return "";

  const cand = [
    post?.postOwnerId,
    post?.userId,
    post?.user_id,
    post?.ownerId,
    post?.authorId,
    post?.createdBy,
    post?.patientId,
    post?.user?._id,
    post?.user?.id,
    post?.user?.userId,
    post?.patient?._id,
    post?.patient?.id,
    post?.author?._id,
    post?.author?.id,
  ];

  // Chercher d'abord un identifiant "propre" (UUID, ObjectId, entier positif)
  for (const x of cand) {
    const id = toIdOrUndef(extractRawId(x));
    if (id !== undefined) return lower(id);
  }

  // Dernier recours : n'importe quelle valeur non vide, même si son format
  // n'est reconnu par aucune règle ci-dessus.
  for (const id of cand) {
    const s = toKey(extractRawId(id));
    if (s) return lower(s);
  }

  return "";
};

// 🐛 FIX PRINCIPAL (ce ticket) : résolution de l'id du POST lui-même.
// `id_post` (ex: "POST_xxxxxxxx") est l'identifiant CANONIQUE utilisé par
// ms-ai-moderation et par tout le reste de l'app (patient QuestionsList,
// feed général PostCard) pour clé les commentaires/likes (`postId`).
// Cette carte donnait la priorité à `post.id`/`post._id` (l'ObjectId Mongo
// interne renvoyé en plus par l'API) : les commentaires/likes créés depuis
// cette page étaient donc enregistrés sous une clé `postId` différente de
// celle utilisée par la page patient, si bien que chaque côté ne voyait
// jamais les commentaires/likes de l'autre. `id_post` doit être vérifié en
// PREMIER pour que les deux vues partagent exactement le même `postId`.
const getPostId = (post) => {
  if (!post) return undefined;

  const candidates = [
    post?.id_post,
    post?.postId,
    post?.post_id,
    post?.postID,
    post?._id,
    post?.id,
    post?.uuid,
    post?.raw?.id_post,
    post?.raw?.postId,
    post?.raw?._id,
    post?.raw?.id,
    post?.data?.id,
    post?.data?._id,
  ];

  for (const c of candidates) {
    const raw = extractRawId(c);
    const key = toKey(raw);
    if (key && key.toLowerCase() !== "[object object]") return key;
  }

  return undefined;
};

// ✅ Definir placeholder names fora do componente (constante)
const PLACEHOLDER_NAMES = new Set([
  "inconnu", "unknown", "anonyme", "anonymous", "dr", "doctor", "médecin", "medecin",
  "patient", "utilisateur", "user", "n/a", "na", "none", "null", "undefined",
  "", " ", "utilisateur inconnu", "patient inconnu", "médecin inconnu",
]);

const isPlaceholderName = (name) => {
  if (!name) return true;
  const clean = String(name).toLowerCase().trim();
  return PLACEHOLDER_NAMES.has(clean);
};

/* ================= Component ================= */
export default function DoctorQuestionsCard({
  post,
  onTranslate,
  currentDoctorId,
  currentDoctorName,
  currentDoctorAvatar,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // 🐛 FIX PRINCIPAL: remplace l'ancien calcul limité par getPostId(),
  // qui teste beaucoup plus de champs possibles et dépile les objets Mongo.
  const postId = useMemo(() => getPostId(post), [post]);

  // AI answer generated for this question (ms-ai-moderation) — was never
  // read/rendered on the doctor side before, unlike the patient feed.
  const aiResponseText =
    post?.aiResponseText ?? post?.ai_response ?? post?.aiResponse ?? "";

  const [doctorsById, setDoctorsById] = useState({});

  const postOwnerId =
    post?.postOwnerId ??
    post?.user?._id ??
    post?.user?.id ??
    post?.userId ??
    post?.user_id ??
    post?.ownerId ??
    post?.authorId ??
    post?.createdBy ??
    null;

  // ✅ FIX: toIdOrUndef (permissif) au lieu de toIntOrUndef (strict UUID/int only)
  const doctorId = useMemo(() => toIdOrUndef(currentDoctorId), [currentDoctorId]);

  const connectedDoctorAvatarUrl = useMemo(
    () => resolveDoctorAvatar(currentDoctorAvatar) || getMediaUrl(currentDoctorAvatar) || null,
    [currentDoctorAvatar]
  );

  const [showComments, setShowComments] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const cardRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  const mediaUrl = useMemo(() => getMediaUrl(post?.photo), [post?.photo]);
  const postPatientId = useMemo(() => getPostPatientId(post), [post]);

  const [patientUser, setPatientUser] = useState(() => post?.user || null);
  const fetchedRef = useRef(new Set());

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );

    if (cardRef.current) observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, []);

  // ✅ FIX: dépendances stabilisées (valeurs primitives, pas l'objet post.user
  // qui change de référence à chaque re-render du parent et perturbait le fetch)
  const postUserFirstName = post?.user?.firstName || "";
  const postUserLastName = post?.user?.lastName || "";

  useEffect(() => {
    // ✅ Ignorer post.user si c'est un placeholder ("Médecin", "Inconnu", etc.)
    if (post?.user) {
      const firstName = postUserFirstName.trim();
      if (!isPlaceholderName(firstName)) {
        setPatientUser(post.user);
        return;
      }
    }

    // ✅ Récupérer l'utilisateur via l'ID résolu (élargi, cf. getPostPatientId)
    let userIdToFetch = postPatientId;
    if (!userIdToFetch) {
      const fallback = toIdOrUndef(
        extractRawId(post?.user_id || post?.userId || post?.postOwnerId || post?.ownerId || post?.patientId)
      );
      userIdToFetch = fallback ? lower(fallback) : "";
    }

    if (!userIdToFetch) {
      // Pas d'ID exploitable dans le post : on garde le fallback "Utilisateur"
      // sans spammer la console (attendu pour certains posts legacy).
      return;
    }

    const cachedUser = patientUserCache.get(userIdToFetch);
    if (cachedUser) {
      setPatientUser(cachedUser);
      return;
    }

    // ✅ FIX (bug principal): si cet ID a déjà échoué récemment (404 / user
    // supprimé), on ne retente pas. Avant, le `.catch` plus bas faisait
    // `fetchedRef.current.delete(userIdToFetch)`, ce qui relançait le fetch
    // à chaque re-render et provoquait la rafale de 404 visible en console.
    if (isUserFetchBlacklisted(userIdToFetch)) return;

    if (fetchedRef.current.has(userIdToFetch)) return;
    fetchedRef.current.add(userIdToFetch);

    const action =
      fetchUserById.length === 1
        ? fetchUserById(userIdToFetch)
        : fetchUserById({ id: userIdToFetch });

          console.log("DEBUG postId:", postId, "| post brut:", post);


    let active = true;
    const promise =
      patientUserPromises.get(userIdToFetch) ||
      dispatch(action)
        .unwrap?.()
        .then((u) => {
          if (u) patientUserCache.set(userIdToFetch, u);
          return u;
        })
        .catch((err) => {
          // ✅ On mémorise l'échec au lieu de le laisser retenter indéfiniment.
          markUserFetchFailed(userIdToFetch);
          console.debug(
            `[DoctorQuestionsCard] utilisateur introuvable (${userIdToFetch}), fallback appliqué`,
            err?.message || err
          );
          return undefined;
        })
        .finally(() => {
          // Libère le slot de la promesse une fois résolue pour permettre un
          // nouveau fetch après expiration du TTL négatif, sans dupliquer
          // les requêtes en vol.
          patientUserPromises.delete(userIdToFetch);
        });

    patientUserPromises.set(userIdToFetch, promise);

    promise.then((u) => {
      if (active && u) setPatientUser(u);
    });

    return () => {
      active = false;
    };
  }, [dispatch, postPatientId, post?.user, postUserFirstName, postUserLastName]);

  const { avatarSrc } = useUserInformation(patientUser);

  const authorName = useMemo(() => {
    if (patientUser) {
      const firstName = (patientUser.firstName || "").trim();
      const lastName = (patientUser.lastName || "").trim();

      if (!isPlaceholderName(firstName) && (firstName || lastName)) {
        const full = `${firstName} ${lastName}`.trim();
        if (!isPlaceholderName(full)) return full;
      }

      const alt = patientUser.fullName || patientUser.name || patientUser.displayName || patientUser.username || patientUser.email;
      if (alt && !isPlaceholderName(alt)) return alt;
    }

    if (post?.user) {
      const firstName = (post.user.firstName || "").trim();
      const lastName = (post.user.lastName || "").trim();

      if (!isPlaceholderName(firstName) && (firstName || lastName)) {
        const full = `${firstName} ${lastName}`.trim();
        if (!isPlaceholderName(full)) return full;
      }

      const alt = post.user.fullName || post.user.name || post.user.displayName || post.user.username || post.user.email;
      if (alt && !isPlaceholderName(alt)) return alt;
    }

    const directName = post?.fullName || post?.authorName || post?.userName || post?.patientName;
    if (directName && !isPlaceholderName(directName)) return directName;

    return "Utilisateur";
  }, [patientUser, post]);

  const authorAvatar = useMemo(() => {
    if (patientUser) {
      const userAvatar = pickAvatar(patientUser);
      if (userAvatar) {
        const url = getMediaUrl(userAvatar);
        if (url) return url;
      }
    }

    if (post?.user && post.user.avatar) {
      const url = getMediaUrl(post.user.avatar);
      if (url) return url;
    }

    const fromPost =
      getMediaUrl(post?.photo) ||
      getMediaUrl(post?.avatar) ||
      getMediaUrl(post?.image) ||
      getMediaUrl(post?.profileImage) ||
      getMediaUrl(post?.profilePicture) ||
      getMediaUrl(post?.author?.avatar) ||
      getMediaUrl(post?.author?.photo);

    if (fromPost) return fromPost;

    const fromHook = getMediaUrl(avatarSrc);
    if (fromHook && fromHook !== DEFAULT_AVATAR) return fromHook;

    return DEFAULT_AVATAR;
  }, [avatarSrc, patientUser, post?.photo, post?.avatar, post?.image, post?.user, post?.author?.avatar, post?.author?.photo]);

  const authorRole = patientUser?.role || post?.user?.role || "patient";

  const isDoctorBadge =
    authorRole?.toLowerCase?.().includes("doctor") ||
    authorRole?.toLowerCase?.().includes("médecin") ||
    authorRole?.toLowerCase?.().includes("medecin") ||
    authorRole?.toLowerCase?.().includes("dr");

  /* ================= Hooks: Likes/Comments ================= */
  const {
    liked,
    count: likeCount,
    loading: likeLoading,
    error: likeError,
    handleToggleLike,
  } = useLikePost(postId, doctorId, postOwnerId);

  const [localLiked, setLocalLiked] = useState(false);
  const [localLikeCount, setLocalLikeCount] = useState(
    Number.isFinite(Number(post?.likesCount))
      ? Number(post.likesCount)
      : Number.isFinite(Number(post?.likes))
      ? Number(post.likes)
      : 0
  );

  useEffect(() => {
    if (typeof liked === "boolean") setLocalLiked(liked);
  }, [liked]);

  useEffect(() => {
    if (Number.isFinite(Number(likeCount))) {
      setLocalLikeCount(Number(likeCount));
    }
  }, [likeCount]);

  const displayLikeCount = localLikeCount;

  const {
    comments,
    isLoading: loadingComments,
    error: commentsError,
    commentsCount: totalComments,
    fetchComments,
    addComment,
    editComment,
    removeComment,
  } = useComments(postId);

  const orderedComments = useMemo(() => {
    if (!Array.isArray(comments)) return [];
    return comments
      .map((comment, index) => ({ comment, index }))
      .sort((left, right) => {
        const doctorOrder =
          Number(isDoctorComment(right.comment)) -
          Number(isDoctorComment(left.comment));
        return doctorOrder || left.index - right.index;
      })
      .map(({ comment }) => comment);
  }, [comments]);

  useEffect(() => {
    if (showComments && postId) fetchComments();
  }, [showComments, postId, fetchComments]);

  /* ================= Comment users cache ================= */
  const [commentUsersById, setCommentUsersById] = useState({});
  const fetchedCommentUsersRef = useRef(new Set());
  const pendingCommentUsersRef = useRef(new Set());

  const [commentDoctorsById, setCommentDoctorsById] = useState({});

  useEffect(() => {
    if (!showComments || !postId) return;

    const loadDoctors = async () => {
      if (doctorsCache.data) {
        const map = {};
        for (const doc of doctorsCache.data) {
          if (doc?.id != null) map[doc.id] = doc;
        }
        setDoctorsById(map);
        return;
      }

      if (doctorsCache.promise) {
        const list = await doctorsCache.promise;
        if (!list) return;
        const map = {};
        for (const doc of list) {
          if (doc?.id != null) map[doc.id] = doc;
        }
        setDoctorsById(map);
        return;
      }

      doctorsCache.promise = dispatch(fetchDoctors())
        .unwrap()
        .then((list) => {
          const items = Array.isArray(list) ? list : [];
          doctorsCache.data = items;
          return items;
        })
        .catch(() => null);

      const list = await doctorsCache.promise;
      if (!list) return;
      const map = {};
      for (const doc of list) {
        if (doc?.id != null) map[doc.id] = doc;
      }
      setDoctorsById(map);
    };

    loadDoctors().catch(console.error);
  }, [dispatch, postId, showComments]);

  useEffect(() => {
    if (!showComments || !Array.isArray(comments) || comments.length === 0) return;

    for (const c of comments) {
      const pid = getCommentPatientId(c);
      if (!pid) continue;
      if (commentUsersById[pid]) continue;
      if (fetchedCommentUsersRef.current.has(pid)) continue;
      if (pendingCommentUsersRef.current.has(pid)) continue;
      // ✅ FIX: même cache négatif que pour patientUser, pour éviter de
      // re-fetcher un auteur de commentaire déjà connu comme introuvable
      // à chaque fois que la liste de commentaires se re-rend.
      if (isUserFetchBlacklisted(pid)) continue;

      pendingCommentUsersRef.current.add(pid);
      const action =
        fetchUserById.length === 1
          ? fetchUserById(pid)
          : fetchUserById({ id: pid });

      dispatch(action)
        .unwrap?.()
        .then((u) => {
          fetchedCommentUsersRef.current.add(pid);
          if (u) setCommentUsersById((prev) => ({ ...prev, [pid]: u }));
        })
        .catch(() => {
          fetchedCommentUsersRef.current.add(pid);
          markUserFetchFailed(pid);
        })
        .finally(() => pendingCommentUsersRef.current.delete(pid));
    }
  }, [showComments, comments, dispatch, commentUsersById]);

  const resolveCommentAuthor = useCallback(
    (c) => {
      const isCurrentDoctorComment = sameId(c?.doctorId, doctorId);

      if (isCurrentDoctorComment) {
        const doctorSource =
          doctorsById[c?.doctorId] ||
          doctorsById[doctorId] ||
          null;
        const name =
          doctorSource?.fullName ||
          doctorSource?.name ||
          currentDoctorName ||
          "Doctor";
        const avatar =
          getMediaUrl(doctorSource?.avatar) ||
          getMediaUrl(doctorSource?.raw?.avatar) ||
          connectedDoctorAvatarUrl ||
          DEFAULT_AVATAR;

        return {
          name: name.toLowerCase().startsWith("dr") ? name : `Dr. ${name}`,
          avatar,
          role: "doctor",
        };
      }

      if (c?.user) {
        const u = c.user;
        const userName = pickFullName(u) || "Patient";

        if (sameId(c?.doctorId, doctorId)) {
          return {
            name: currentDoctorName || userName || "Doctor",
            avatar: connectedDoctorAvatarUrl || getMediaUrl(pickAvatar(u)) || DEFAULT_AVATAR,
            role: "doctor",
          };
        }

        return {
          name: userName,
          avatar: getMediaUrl(pickAvatar(u)) || DEFAULT_AVATAR,
          role: u?.role || "user",
        };
      }

      const cDoctorId = toIdOrUndef(c?.doctorId);
      if (cDoctorId != null) {
        const doc = doctorsById[c.doctorId];
        if (doc) {
          const name = doc.fullName || "Doctor";
          const avatar = getMediaUrl(doc.avatar) || DEFAULT_AVATAR;
          return {
            name: name.toLowerCase().startsWith("dr") ? name : `Dr. ${name}`,
            avatar,
            role: "doctor",
          };
        }
        return {
          name: currentDoctorName ? `Dr. ${currentDoctorName}` : "Doctor",
          avatar: connectedDoctorAvatarUrl || DEFAULT_AVATAR,
          role: "doctor",
        };
      }

      const pid = getCommentPatientId(c);
      if (pid) {
        if (
          patientUser &&
          (sameId(pid, patientUser?.id) || sameId(pid, patientUser?._id))
        ) {
          return { name: authorName, avatar: authorAvatar, role: authorRole };
        }

        const u = commentUsersById[pid] || null;
        if (u) {
          return {
            name: pickFullName(u) || "Patient",
            avatar: getMediaUrl(pickAvatar(u)) || DEFAULT_AVATAR,
            role: u?.role || "patient",
          };
        }
      }

      return { name: "Patient", avatar: DEFAULT_AVATAR, role: "patient" };
    },
    [
      doctorId,
      currentDoctorName,
      connectedDoctorAvatarUrl,
      patientUser,
      authorName,
      authorAvatar,
      authorRole,
      commentUsersById,
      commentDoctorsById,
      doctorsById,
    ]
  );

  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState("");
  const [commentMenuOpenId, setCommentMenuOpenId] = useState(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!commentMenuOpenId) return;
      if (!e.target.closest?.(".pcard-comm-moreWrap")) setCommentMenuOpenId(null);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [commentMenuOpenId]);

  const beginEdit = (c) => {
    if (!c) return;
    const authorId = c?.doctorId ?? c?.userId ?? null;
    if (authorId != null && doctorId != null && !sameId(authorId, doctorId)) return;
    setEditingId(c._id || c.id);
    setEditingText(c.content || c.text || "");
    setCommentMenuOpenId(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingText("");
    setCommentMenuOpenId(null);
  };

  const saveEdit = async () => {
    const txt = (editingText || "").trim();
    if (!txt || !editingId) return;
    await editComment(editingId, txt);
    setEditingId(null);
    setEditingText("");
    setCommentMenuOpenId(null);
  };

  const handleDeleteComment = async (id) => {
    if (!id) return;
    setCommentMenuOpenId(null);
    if (!window.confirm(t("homePage.deleteCommentConfirm") || "Delete this comment?")) return;
    await removeComment(id);
    if (editingId === id) {
      setEditingId(null);
      setEditingText("");
    }
  };

  if (!post) return null;

  console.log("DEBUG doctorId:", doctorId, "| currentDoctorId reçu:", currentDoctorId);

  return (
    <div ref={cardRef} className="pcard-card">
      {/* ── Header ── */}
      <div className="pcard-header">
        <div className="pcard-author-section">
          <div className="pcard-avatar">
            <SafeImage
              src={authorAvatar}
              fallback={DEFAULT_AVATAR}
              alt={authorName}
              className="pcard-avatar-img"
            />
          </div>

          <div className="pcard-author-info">
            <div className="pcard-author-name-line">
              <span className="pcard-author-name">{authorName}</span>
              {isDoctorBadge && (
                <span className="pcard-badge-doctor">
                  {t("doctorQuestionsCard.badges.doctor")}
                </span>
              )}
              {post.blocked === false && (
                <span className="pcard-badge-verified">
                  {t("doctorQuestionsCard.badges.verified")}
                </span>
              )}
            </div>
            <div className="pcard-timestamp">
              Posted {timeAgo(post?.date || post?.createdAt)}
            </div>
          </div>

        </div>
      </div>

      {/* ── Content ── */}
      <div className="pcard-content">
        <p className="pcard-text">{post.description}</p>

        {mediaUrl && (
          <div className="pcard-media">
            <SafeImage
              src={mediaUrl}
              fallback={DEFAULT_AVATAR}
              alt={t("doctorQuestionsCard.media.alt")}
              className="pcard-media-img"
            />
          </div>
        )}
      </div>

      {/* ── Footer actions ── */}
      <div className="pcard-footer">
        <div className="pcard-actions-left">
          {/* Like */}
          <button
            type="button"
            className={`pcard-action-btn ${localLiked ? "liked" : ""}`}
            onClick={async () => {
              setSubmitError("");
              if (doctorId == null) {
                setSubmitError("Connexion requise pour liker.");
                return;
              }
              if (!postId) {
                setSubmitError("Impossible d'identifier ce post.");
                return;
              }
              setLocalLiked((prevLiked) => {
                setLocalLikeCount((prevCount) => (prevLiked ? Math.max(0, prevCount - 1) : prevCount + 1));
                return !prevLiked;
              });
              await handleToggleLike();
            }}
            disabled={likeLoading || !postId}
            aria-pressed={localLiked}
            title={likeError ? String(likeError?.message || likeError) : undefined}
          >
            <svg
              className="pcard-action-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
            </svg>
            <span>{t("doctorQuestionsCard.likePost")}</span>
          </button>

          {/* Comment toggle */}
          <button
            type="button"
            className="pcard-action-btn"
            onClick={() => {
              setSubmitError("");
              setShowComments((v) => !v);
            }}
            aria-expanded={showComments}
            disabled={!postId}
          >
            <svg
              className="pcard-action-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M21 15a4 4 0 0 1-4 4H7l-4 4V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" />
            </svg>
            <span>{t("doctorQuestionsCard.comment")}</span>
          </button>
        </div>

        {/* Stats */}
        <div className="pcard-stats">
          <button className="pcard-stat-btn" type="button" disabled>
            <svg
              className="pcard-stat-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
            </svg>
            <span>{Number(displayLikeCount || 0)}</span>
          </button>

          <button
            className="pcard-stat-btn"
            type="button"
            onClick={() => setShowComments((v) => !v)}
          >
            <svg
              className="pcard-stat-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M21 15a4 4 0 0 1-4 4H7l-4 4V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" />
            </svg>
            <span>{Number(totalComments || 0)}</span>
          </button>
        </div>
      </div>

      {/* ── Comments section ── */}
      {showComments && postId && (
        <div className="pcard-comments">
          {/* Composer */}
          <div className="pcard-comm-composer">
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                setSubmitError("");

                const form = e.currentTarget;
                const content = e.currentTarget.comment?.value?.trim?.() || "";
                if (!content) return;

                if (doctorId == null) {
                  setSubmitError("Connexion requise pour commenter.");
                  return;
                }

                const res = await addComment({
                  content,
                  postId,
                  doctorId,
                  ...(postOwnerId ? { postOwnerId } : {}),
                });

                if (!res?.error) {
                  form.reset();
                  await fetchComments();
                }
              }}
            >
              <input
                type="text"
                name="comment"
                className="pcard-comm-input"
                placeholder={
                  t("doctorQuestionsCard.comments.placeholder") ||
                  "Write a comment..."
                }
                required
              />
              <button
                type="submit"
                className="pcard-comm-submit"
                aria-label={t("doctorQuestionsCard.comments.send")}
              >
                <svg
                  className="pcard-send-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
                </svg>
              </button>
            </form>

            {submitError && (
              <div className="pcard-comm-error">{submitError}</div>
            )}
            {commentsError && (
              <div className="pcard-comm-error">
                {String(commentsError?.message || commentsError)}
              </div>
            )}
          </div>

          {/* List */}
          <div className="pcard-comm-list">
            {aiResponseText && <AIResponseBanner text={aiResponseText} t={t} />}

            {aiResponseText && orderedComments.length > 0 && (
              <div className="pcc-ai-divider">
                <span>
                  {t?.("postCard.memberComments") === "postCard.memberComments"
                    ? "Commentaires des membres"
                    : t?.("postCard.memberComments")}
                </span>
              </div>
            )}

            {orderedComments.length > 0 ? (
              orderedComments.map((c) => {
                const info = resolveCommentAuthor(c);
                const id = c._id || c.id;

                const canEditDelete =
                  doctorId != null &&
                  (sameId(c.doctorId, doctorId) ||
                    sameId(c.userId, doctorId) ||
                    sameId(c.user?._id, doctorId) ||
                    sameId(c.user?.id, doctorId));

                const isEditing  = editingId === id;
                const isMenuOpen = commentMenuOpenId === id;

                return (
                  <div key={id} className="pcard-comm-item">
                    {/* ✅ FIX: SafeImage au lieu d'un <img> brut, pour un
                        fallback cohérent avec le reste de la carte. */}
                    <SafeImage
                      src={info.avatar}
                      fallback={DEFAULT_AVATAR}
                      alt={info.name}
                      className="pcard-comm-avatar"
                    />

                    <div className="pcard-comm-body">
                      {!isEditing ? (
                        <>
                          <div className="pcard-comm-header">
                            <div className="pcard-comm-info">
                              <span className="pcard-comm-name">{info.name}</span>
                              {info.role && (
                                <span className="pcard-comm-role">{info.role}</span>
                              )}
                              <span className="pcard-comm-time">
                                {timeAgo(c.createdAt)}
                              </span>
                            </div>

                            {canEditDelete && (
                              <div className="pcard-comm-moreWrap">
                                <button
                                  type="button"
                                  className="pcard-comm-moreBtn"
                                  onClick={() =>
                                    setCommentMenuOpenId((prev) =>
                                      prev === id ? null : id
                                    )
                                  }
                                  title="Options"
                                >
                                  •••
                                </button>

                                {isMenuOpen && (
                                  <div className="pcard-comm-menu" role="menu">
                                    <button
                                      type="button"
                                      className="pcard-comm-menuItem"
                                      role="menuitem"
                                      onClick={() => beginEdit(c)}
                                    >
                                      {t("doctorQuestionsCard.comments.edit") || "Edit"}
                                    </button>
                                    <button
                                      type="button"
                                      className="pcard-comm-menuItem danger"
                                      role="menuitem"
                                      onClick={() => handleDeleteComment(id)}
                                    >
                                      {t("doctorQuestionsCard.comments.delete") || "Delete"}
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>

                          <div className="pcard-comm-text">{c.content}</div>
                        </>
                      ) : (
                        <div className="pcard-edit-area">
                          <textarea
                            className="pcard-edit-textarea"
                            value={editingText}
                            onChange={(e) => setEditingText(e.target.value)}
                          />
                          <div className="pcard-edit-controls">
                            <button
                              className="pcard-edit-btn"
                              onClick={cancelEdit}
                              type="button"
                            >
                              Cancel
                            </button>
                            <button
                              className="pcard-edit-btn primary"
                              onClick={saveEdit}
                              type="button"
                              disabled={!editingText.trim()}
                            >
                              Save
                            </button>
                          </div>

                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            ) : !loadingComments ? (
              <div className="pcard-comm-empty">
                {t("doctorQuestionsCard.comments.empty")}
              </div>
            ) : null}

            {loadingComments && (
              <div className="pcard-comm-loading">
                {t("doctorQuestionsCard.comments.loading")}
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
}